import { lightningParamName } from "@/geojson/definitions";
import type { MapLegendProps } from "@/overlay/components/Legends/LegendsContainer";
import {
  ColorMapLegend,
  CustomColormapMapLegend,
  LightningMapLegend,
  MapLegendControls,
} from "@/overlay/components/Legends/MapLegend";
import { ThresholdLegend } from "@/overlay/components/Legends/ThresholdLegend/ThresholdLegend";
import { useResolvedLegendColormap } from "@/overlay/components/Legends/hooks/useResolvedLegendColormap";
import { t } from "@lingui/core/macro";
import styles from "./MapLegends.module.scss";

export type LegendContentProps = {
  legendProp: MapLegendProps;
  containerWidth: number;
  visibleLegendCount: number;
  toggleLegend: (layerId: number, isVisible: boolean) => void;
};

export const LegendContent: React.FC<LegendContentProps> = ({
  legendProp,
  containerWidth,
  visibleLegendCount,
  toggleLegend,
}) => {
  const legendLabel =
    legendProp.label ?? (legendProp.parameter.includes(lightningParamName) ? "lightnings" : legendProp.parameter);
  const {
    resolvedCustomColormap,
    isLoading: isCustomColormapLoading,
    shouldShowUnavailable,
  } = useResolvedLegendColormap({
    customColormap: legendProp.customColormap,
    customColormapId: legendProp.customColormapId,
  });

  return (
    <div
      key={`${legendProp.parameter}_${legendProp.layerId}`}
      className={styles.element}
      style={{ width: `${containerWidth / visibleLegendCount}px` }}
    >
      <MapLegendControls
        label={legendLabel}
        onHide={() => toggleLegend(legendProp.layerId, legendProp.legendVisible)}
      />
      <div className={styles["map-legend-wrapper"]} data-recording-map-legend-graphic="true">
        {/* TODO: Replace all non-API legends (threshold/static custom colormap) with the new legend component. */}
        {legendProp.parameter.includes("lightnings") ? (
          <LightningMapLegend parameter={legendProp.parameter} />
        ) : legendProp.thresholdItems ? (
          <ThresholdLegend items={legendProp.thresholdItems} />
        ) : resolvedCustomColormap ? (
          <CustomColormapMapLegend
            parameter={legendProp.parameter}
            customColormap={resolvedCustomColormap}
            legendUnit={legendProp.legendUnit}
          />
        ) : isCustomColormapLoading ? (
          <></>
        ) : shouldShowUnavailable ? (
          <div>{t`Custom legend unavailable`}</div>
        ) : (
          <ColorMapLegend parameter={legendProp.parameter} style={legendProp.style} />
        )}
      </div>
    </div>
  );
};
